import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listActivity } from '../../api/activityApi';
import { isAiProposalsEnabled } from '../../helpers/agentFeature';

/* AI Activity — the execution ledger's human-readable face. Read-only: every
   row's summary/detail is composed backend-side from real ledger fields
   (counters-from-truth), so we render them verbatim and never fabricate. */

const prettyType = (t) => String(t || 'action')
  .replace(/^agent\./, '')
  .replace(/[_.]/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

// state -> visual tone. Unknown states fall back to a neutral chip.
const STATE_TONE = {
  executed: 'var(--success)',
  confirmed: 'var(--success)',
  delivered: 'var(--success)',
  failed: 'var(--danger)',
  error: 'var(--danger)',
  declined: 'var(--text-muted)',
  expected_no_op: 'var(--text-muted)',
  no_op: 'var(--text-muted)',
  shadow: 'var(--warning)',
  requested: 'var(--accent)',
  pending: 'var(--accent)',
};
const toneFor = (state) => STATE_TONE[state] || 'var(--text-muted)';
const stateLabel = (s) => String(s || '').replace(/_/g, ' ');

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
}

const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const S = {
  page: { padding: '22px 28px', maxWidth: 860, margin: '0 auto' },
  h1: { fontSize: 20, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' },
  sub: { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 },
  glabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--text-muted)', marginRight: 2 },
  chip: (active) => ({
    padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: '1px solid var(--border)',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? 'var(--accent-ink)' : 'var(--text-main)',
  }),
  dateInput: {
    padding: '4px 8px', borderRadius: 8, border: '1px solid var(--border)',
    background: 'var(--bg-panel)', color: 'var(--text-main)', fontSize: 12,
  },
  linkBtn: { background: 'transparent', border: 'none', color: 'var(--accent)', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 },
  feed: { marginTop: 12, borderTop: '1px solid var(--border)' },
  row: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 4px', borderBottom: '1px solid var(--border)' },
  dot: (tone) => ({ width: 9, height: 9, borderRadius: '50%', background: tone, marginTop: 6, flexShrink: 0 }),
  rowBody: { flex: 1, minWidth: 0 },
  rowTop: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  summary: { fontSize: 14, fontWeight: 600, color: 'var(--text-main)' },
  stateChip: (tone) => ({ fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', color: tone, border: `1px solid ${tone}`, borderRadius: 999, padding: '1px 7px' }),
  detail: { fontSize: 13, color: 'var(--text-muted)', marginTop: 2 },
  rowMeta: { fontSize: 12, color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' },
  time: { fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0 },
  empty: { textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, padding: '48px 20px', lineHeight: 1.6 },
  loadMore: { display: 'block', margin: '16px auto 0', padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  note: { fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 12 },
};

export default function ActivityFeed() {
  const enabled = isAiProposalsEnabled();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_action_type: {}, by_state: {} });
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [state, setState] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [day, setDay] = useState(null);

  const load = useCallback(async (append = false, cursor = null) => {
    setLoading(true); setError(null);
    try {
      const { data } = await listActivity({ state, action_type: actionType, day, cursor });
      const items = Array.isArray(data?.activity) ? data.activity : [];
      setRows((prev) => append ? [...prev, ...items] : items);
      setCounts(data?.counts || { total: items.length, by_action_type: {}, by_state: {} });
      setNext(data?.next || null);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'The activity service isn’t available yet.' : 'Couldn’t load activity.');
    } finally {
      setLoading(false);
    }
  }, [state, actionType, day]);

  useEffect(() => { if (enabled) load(false, null); }, [enabled, load]);

  const stateKeys = useMemo(() => Object.keys(counts.by_state || {}), [counts]);
  const typeKeys = useMemo(() => Object.keys(counts.by_action_type || {}), [counts]);

  if (!enabled) {
    return (
      <div style={S.page}>
        <h1 style={S.h1}>AI Activity</h1>
        <p style={S.empty}>The assistant isn’t enabled for this workspace yet.</p>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>AI Activity</h1>
      <p style={S.sub}>What the assistant and watchers actually did — every row from the execution ledger.</p>

      {stateKeys.length > 0 && (
        <div style={S.filters}>
          <span style={S.glabel}>State</span>
          <span style={S.chip(!state)} onClick={() => setState(null)} role="button">All</span>
          {stateKeys.map((k) => (
            <span key={k} style={S.chip(state === k)} onClick={() => setState(state === k ? null : k)} role="button">
              {stateLabel(k)} ({counts.by_state[k]})
            </span>
          ))}
        </div>
      )}
      {typeKeys.length > 0 && (
        <div style={S.filters}>
          <span style={S.glabel}>Type</span>
          <span style={S.chip(!actionType)} onClick={() => setActionType(null)} role="button">All</span>
          {typeKeys.map((k) => (
            <span key={k} style={S.chip(actionType === k)} onClick={() => setActionType(actionType === k ? null : k)} role="button">
              {prettyType(k)} ({counts.by_action_type[k]})
            </span>
          ))}
        </div>
      )}
      <div style={S.filters}>
        <span style={S.glabel}>Day</span>
        <input
          type="date"
          style={S.dateInput}
          value={day || ''}
          max={todayStr()}
          onChange={(e) => setDay(e.target.value || null)}
        />
        {day && <button style={S.linkBtn} onClick={() => setDay(null)}>Clear</button>}
        {!day && <button style={S.linkBtn} onClick={() => setDay(todayStr())}>Today</button>}
      </div>

      {error && <p style={{ ...S.note, color: 'var(--danger)' }}>{error}</p>}

      {loading && rows.length === 0 ? (
        <p style={S.empty}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={S.empty}>No activity yet{day ? ' for this day' : ''}. Actions the assistant and watchers take will show here.</p>
      ) : (
        <>
          <div style={S.feed}>
            {rows.map((r) => {
              const tone = toneFor(r.state);
              return (
                <div style={S.row} key={r.id}>
                  <span style={S.dot(tone)} aria-hidden="true" />
                  <div style={S.rowBody}>
                    <div style={S.rowTop}>
                      <span style={S.summary}>{r.summary || prettyType(r.action_type)}</span>
                      {r.state && <span style={S.stateChip(tone)} title={r.no_op_reason || undefined}>{stateLabel(r.state)}</span>}
                    </div>
                    {r.detail && <div style={S.detail}>{r.detail}</div>}
                    {(r.subject?.label || r.surface) && (
                      <div style={S.rowMeta}>
                        {r.subject?.label && <span>{r.subject.label}</span>}
                        {r.surface && <span>· {r.surface}</span>}
                      </div>
                    )}
                  </div>
                  <span style={S.time}>{fmtWhen(r.created_at)}</span>
                </div>
              );
            })}
          </div>
          {next && (
            <button style={S.loadMore} onClick={() => load(true, next)} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
